import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createApiClient, type Session, type UpdateUserProfileRequest, type UserProfile } from "@floline/api-client";
import { API_URL } from "@/env";
import { SESSION_QUERY_KEY } from "./use-session";

const apiClient = createApiClient(API_URL);

function hasSessionKeyPart(keyPart: unknown): boolean {
  return typeof keyPart === "string" && keyPart.toLowerCase().includes("session");
}

export const userProfileKeys = {
  all: ["user-profile"] as const,
};

export function useUserProfile() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: userProfileKeys.all,
    queryFn: async () => {
      const response = await apiClient.getUserProfile();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: async (data: UpdateUserProfileRequest) => {
      const response = await apiClient.updateUserProfile(data);
      return response.data;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData<UserProfile>(userProfileKeys.all, data);
      queryClient.setQueryData<Session | null>(SESSION_QUERY_KEY, (prev) => {
        if (!prev) return prev;
        const fullName = `${data.firstName} ${data.lastName}`.trim();
        return {
          ...prev,
          user: {
            ...prev.user,
            name: fullName,
            email: data.email,
          },
        };
      });

      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.some(hasSessionKeyPart),
      });
    },
  });

  return {
    profile: query.data,
    isLoadingProfile: query.isLoading,
    updateProfile: mutation.mutateAsync,
    isUpdatingProfile: mutation.isPending,
    updateProfileError: mutation.error,
  };
}
